import React, { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const PLAN_COLOR = "#c4a574";
const FACT_COLOR = "#8aa0b8";

function formatNumber(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("ru-RU", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
}

function formatPct(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toLocaleString("ru-RU", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

function formatDay(value) {
  if (!value) return "";
  const [, month, day] = String(value).slice(0, 10).split("-");
  return day && month ? `${day}.${month}` : value;
}

function eachIsoDay(from, to) {
  const days = [];
  if (!from || !to || from > to) return days;
  const current = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, "0");
    const day = String(current.getDate()).padStart(2, "0");
    days.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function MetricCard({ label, value, hint }) {
  return (
    <article className="dash-metric">
      <p className="dash-metric-label">{label}</p>
      <p className="dash-metric-value">{value}</p>
      {hint ? <p className="dash-metric-hint">{hint}</p> : null}
    </article>
  );
}

function PeriodTooltip({ active, payload, label, isPercent }) {
  if (!active) return null;
  const formatValue = isPercent ? formatPct : formatNumber;
  const items = (payload || []).filter((item) => item?.value != null);
  return (
    <div className="dash-chart-tooltip">
      <p className="dash-chart-tooltip-date">{label}</p>
      {items.length ? (
        items.map((item) => (
          <p key={item.dataKey}>
            {item.name ? `${item.name}: ` : null}
            <strong>{formatValue(item.value)}</strong>
          </p>
        ))
      ) : (
        <p>
          <strong>нет отчёта</strong>
        </p>
      )}
    </div>
  );
}

function PeriodChart({
  title,
  dateFrom,
  dateTo,
  series,
  dataKey,
  isPercent,
  latest,
}) {
  const formatValue = isPercent ? formatPct : formatNumber;
  const byDate = Object.fromEntries((series || []).map((item) => [item.date, item]));
  const data = eachIsoDay(dateFrom, dateTo).map((date) => {
    const row = byDate[date];
    const raw = row ? row[dataKey] : null;
    return {
      date,
      label: formatDay(date),
      value: raw == null || raw === "" ? null : Number(raw),
    };
  });
  if (!data.length) return null;

  return (
    <section className="dash-panel dash-side-chart">
      <div className="dash-panel-head">
        <h2>{title}</h2>
        {latest != null ? (
          <span className="dash-panel-total">{formatValue(latest)}</span>
        ) : null}
      </div>
      <div className="dash-side-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={isPercent ? (value) => formatPct(value) : formatNumber}
              width={isPercent ? 52 : 56}
              allowDecimals={isPercent}
            />
            <Tooltip
              content={(props) => (
                <PeriodTooltip {...props} isPercent={isPercent} />
              )}
            />
            <Bar
              dataKey="value"
              name={title}
              fill={FACT_COLOR}
              radius={[4, 4, 0, 0]}
              maxBarSize={36}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function DualPeriodChart({
  title,
  dateFrom,
  dateTo,
  series,
  keys,
  labels,
  colors,
  isPercent,
}) {
  const byDate = Object.fromEntries((series || []).map((item) => [item.date, item]));
  const data = eachIsoDay(dateFrom, dateTo).map((date) => {
    const row = byDate[date];
    const first = row ? row[keys[0]] : null;
    const second = row ? row[keys[1]] : null;
    return {
      date,
      label: formatDay(date),
      [keys[0]]: first == null || first === "" ? null : Number(first),
      [keys[1]]: second == null || second === "" ? null : Number(second),
    };
  });
  if (!data.length) return null;

  return (
    <section className="dash-panel dash-side-chart">
      <div className="dash-panel-head">
        <h2>{title}</h2>
      </div>
      <div className="dash-side-chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={isPercent ? (value) => formatPct(value) : formatNumber}
              width={isPercent ? 52 : 56}
              allowDecimals={isPercent}
            />
            <Tooltip
              content={(props) => (
                <PeriodTooltip {...props} isPercent={isPercent} />
              )}
            />
            <Legend />
            <Bar
              dataKey={keys[0]}
              name={labels[0]}
              fill={colors[0]}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
            />
            <Bar
              dataKey={keys[1]}
              name={labels[1]}
              fill={colors[1]}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function heatStyle(ratio) {
  if (ratio == null || !Number.isFinite(ratio)) {
    return undefined;
  }
  if (Math.abs(ratio - 1) < 0.02) {
    return { background: "rgba(138, 160, 184, 0.22)" };
  }
  if (ratio < 1) {
    const strength = Math.min(1, (1 - ratio) / 0.3);
    return { background: `rgba(234, 88, 12, ${0.12 + strength * 0.38})` };
  }
  const strength = Math.min(1, (ratio - 1) / 0.3);
  return { background: `rgba(22, 163, 74, ${0.12 + strength * 0.38})` };
}

function RestaurantHeatmap({
  title,
  extra,
  dates,
  rows,
  getValue,
  getRatio,
  formatCell,
  renderCell,
  tooltip,
}) {
  if (!dates?.length || !rows?.length) return null;

  return (
    <section className="dash-panel">
      <div className="dash-panel-head">
        <h2>{title}</h2>
        {extra}
      </div>
      <div className="dash-table-wrap dash-col-hr-heat-wrap">
        <table className="dash-table dash-col-hr-heat">
          <thead>
            <tr>
              <th>Ресторан</th>
              {dates.map((date) => (
                <th key={date} className="is-num">
                  {formatDay(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const byDate = Object.fromEntries(
                (row.days || []).map((item) => [item.date, item]),
              );
              return (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  {dates.map((date) => {
                    const day = byDate[date];
                    const value = day && getValue ? getValue(day) : null;
                    const ratio = day ? getRatio(day) : null;
                    const titleText = day ? tooltip(row.name, date, day) : "нет отчёта";
                    const content = day
                      ? renderCell
                        ? renderCell(day)
                        : value == null
                          ? null
                          : formatCell(value)
                      : null;
                    return (
                      <td
                        key={date}
                        className="is-num dash-col-hr-heat-cell"
                        style={heatStyle(ratio)}
                        title={titleText}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RestaurantCompareChart({
  title,
  hint,
  items,
  keys,
  labels,
  colors,
  isPercent,
  reference,
}) {
  const data = (items || [])
    .map((item) => ({
      name: item.name,
      [keys[0]]: item[keys[0]] == null ? null : Number(item[keys[0]]),
      [keys[1]]: item[keys[1]] == null ? null : Number(item[keys[1]]),
    }))
    .filter((item) => item[keys[0]] != null || item[keys[1]] != null);
  if (!data.length) return null;

  const height = Math.max(280, data.length * 28 + 56);

  return (
    <section className="dash-panel">
      <div className="dash-panel-head">
        <h2>{title}</h2>
        {hint ? <span className="dash-panel-total">{hint}</span> : null}
      </div>
      <div className="dash-col-hr-compare-wrap" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 8, right: 16, left: 4, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickFormatter={isPercent ? (value) => formatPct(value) : formatNumber}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={{ fontSize: 11, fill: "#6b7280" }}
            />
            {reference != null ? (
              <ReferenceLine
                x={reference}
                stroke="#9ca3af"
                strokeDasharray="4 4"
              />
            ) : null}
            <Tooltip
              content={(props) => (
                <PeriodTooltip {...props} isPercent={isPercent} />
              )}
            />
            <Legend />
            <Bar
              dataKey={keys[0]}
              name={labels[0]}
              fill={colors[0]}
              radius={[0, 4, 4, 0]}
              maxBarSize={12}
            />
            <Bar
              dataKey={keys[1]}
              name={labels[1]}
              fill={colors[1]}
              radius={[0, 4, 4, 0]}
              maxBarSize={12}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

const RESERVE_CHARTS = [
  { key: "reserve_mng", title: "Кадровый резерв мнг", latestKey: "mng" },
  { key: "reserve_tn", title: "Кадровый резерв ТН", latestKey: "tn" },
  { key: "reserve_pct", title: "% кадрового резерва", latestKey: "pct" },
];

const ColHrDashboard = ({ summary }) => {
  const restaurants = summary.restaurants || [];
  const restaurantDays = summary.restaurant_days || [];
  const loadedDates = summary.loaded_dates || [];
  const reserve = summary.reserve || {};
  const series = summary.series || [];
  const oneRestaurant = summary.restaurant_id != null;
  const [ftrMetric, setFtrMetric] = useState("ftr_chk");
  const snapshotHint = summary.snapshot_date
    ? `на ${formatDay(summary.snapshot_date)}`
    : "";
  const periodHint = "среднее за период";

  return (
    <div className="dash-col-hr">
      <section className="dash-metrics">
        <MetricCard
          label="Кадровый резерв мнг"
          value={formatPct(reserve.mng)}
          hint={snapshotHint}
        />
        <MetricCard
          label="Кадровый резерв ТН"
          value={formatPct(reserve.tn)}
          hint={snapshotHint}
        />
        <MetricCard
          label="% кадрового резерва"
          value={formatPct(reserve.pct)}
          hint={snapshotHint}
        />
      </section>

      <div className="dash-hr-charts">
        {RESERVE_CHARTS.map((chart) => (
          <PeriodChart
            key={chart.key}
            title={chart.title}
            dateFrom={summary.date_from}
            dateTo={summary.date_to}
            series={series}
            dataKey={chart.key}
            isPercent
            latest={reserve[chart.latestKey]}
          />
        ))}
      </div>

      {oneRestaurant ? (
        <div className="dash-col-hr-compare">
          <DualPeriodChart
            title="SPMH по дням"
            dateFrom={summary.date_from}
            dateTo={summary.date_to}
            series={series}
            keys={["spmh_plan", "spmh_fact"]}
            labels={["План", "Факт"]}
            colors={[PLAN_COLOR, FACT_COLOR]}
          />
          <DualPeriodChart
            title="Заполненность по дням"
            dateFrom={summary.date_from}
            dateTo={summary.date_to}
            series={series}
            keys={["ftr_chk", "ftr_mng"]}
            labels={["ЧК", "Мнг"]}
            colors={[PLAN_COLOR, FACT_COLOR]}
            isPercent
          />
        </div>
      ) : null}

      {restaurantDays.length > 0 && loadedDates.length > 0 ? (
        <>
          <RestaurantHeatmap
            title="SPMH по дням"
            extra={<span className="dash-panel-total">факт, цвет = % плана</span>}
            dates={loadedDates}
            rows={restaurantDays}
            getValue={(day) => day.spmh_fact}
            getRatio={(day) => {
              const plan = Number(day.spmh_plan);
              const fact = Number(day.spmh_fact);
              if (!Number.isFinite(plan) || !plan || !Number.isFinite(fact)) {
                return null;
              }
              return fact / plan;
            }}
            formatCell={formatNumber}
            renderCell={(day) => {
              if (day.spmh_fact == null && day.spmh_plan == null) return null;
              return (
                <>
                  <span className="dash-col-hr-heat-fact">
                    {formatNumber(day.spmh_fact)}
                  </span>
                  <span className="dash-col-hr-heat-plan">
                    план {formatNumber(day.spmh_plan)}
                  </span>
                </>
              );
            }}
            tooltip={(name, date, day) => {
              const plan = Number(day.spmh_plan);
              const fact = Number(day.spmh_fact);
              const pct =
                Number.isFinite(plan) && plan && Number.isFinite(fact)
                  ? formatPct((fact / plan) * 100)
                  : "—";
              return `${name}, ${formatDay(date)}: факт ${formatNumber(
                day.spmh_fact,
              )}, план ${formatNumber(day.spmh_plan)}, ${pct} плана`;
            }}
          />
          <RestaurantHeatmap
            title="Заполненность по дням"
            extra={
              <div className="dash-col-hr-toggle" role="group" aria-label="Показатель заполненности">
                <button
                  type="button"
                  className={ftrMetric === "ftr_chk" ? "is-active" : ""}
                  onClick={() => setFtrMetric("ftr_chk")}
                >
                  ЧК
                </button>
                <button
                  type="button"
                  className={ftrMetric === "ftr_mng" ? "is-active" : ""}
                  onClick={() => setFtrMetric("ftr_mng")}
                >
                  Мнг
                </button>
              </div>
            }
            dates={loadedDates}
            rows={restaurantDays}
            getValue={(day) => day[ftrMetric]}
            getRatio={(day) => {
              const value = Number(day[ftrMetric]);
              return Number.isFinite(value) ? value / 100 : null;
            }}
            formatCell={formatPct}
            tooltip={(name, date, day) =>
              `${name}, ${formatDay(date)}: ${formatPct(day[ftrMetric])}`
            }
          />
        </>
      ) : null}

      {restaurants.length > 0 ? (
        <div className="dash-col-hr-compare">
          <RestaurantCompareChart
            title="SPMH по ресторанам"
            hint={periodHint}
            items={restaurants}
            keys={["spmh_plan", "spmh_fact"]}
            labels={["План", "Факт"]}
            colors={[PLAN_COLOR, FACT_COLOR]}
          />
          <RestaurantCompareChart
            title="Заполненность по ресторанам"
            hint={periodHint}
            items={restaurants}
            keys={["ftr_chk", "ftr_mng"]}
            labels={["ЧК", "Мнг"]}
            colors={[PLAN_COLOR, FACT_COLOR]}
            isPercent
            reference={100}
          />
        </div>
      ) : null}

      {restaurants.length > 0 ? (
        <section className="dash-panel">
          <div className="dash-panel-head">
            <h2>Рестораны</h2>
            <span className="dash-panel-total">{periodHint}</span>
          </div>
          <div className="dash-table-wrap">
            <table className="dash-table dash-table-wide">
              <thead>
                <tr>
                  <th>Ресторан</th>
                  <th className="is-num">SPMH план</th>
                  <th className="is-num">SPMH факт</th>
                  <th className="is-num">Заполненность ЧК</th>
                  <th className="is-num">Заполненность мнг</th>
                </tr>
              </thead>
              <tbody>
                {restaurants.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td className="is-num">{formatNumber(item.spmh_plan)}</td>
                    <td className="is-num">{formatNumber(item.spmh_fact)}</td>
                    <td className="is-num">{formatPct(item.ftr_chk)}</td>
                    <td className="is-num">{formatPct(item.ftr_mng)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="dash-legend">
        <h2>Как считаются цифры</h2>
        <p>
          Берём загрузки COL за выбранные даты
          {summary.loaded_dates?.length
            ? ` (есть отчёты за ${summary.loaded_dates
                .map(formatDay)
                .join(", ")})`
            : ""}
          . День без файла на графике пустой.
        </p>
        <dl>
          <div>
            <dt>Кадровый резерв</dt>
            <dd>
              Карточки — снимок последнего дня с загрузкой
              {summary.snapshot_date
                ? ` (${formatDay(summary.snapshot_date)})`
                : ""}
              . Столбцы ниже — значение из отчёта за каждый день, без
              суммирования. Это сетевые доли: менеджеры, тренеры и общий
              процент (AL13). Если выбрана одна точка, резерв всё равно по сети.
            </dd>
          </div>
          <div>
            <dt>SPMH и заполненность</dt>
            <dd>
              Теплокарта — каждый день с файлом отдельно: SPMH показывает факт,
              цвет = факт ÷ план; заполненность переключается ЧК / мнг, цвет
              относительно 100%. Пустой день без колонки. Горизонтальные
              графики и таблица — среднее по дням с данными у точки, не
              последний день и не сумма. Если выбрана одна точка,
              дополнительно рисуем её план/факт и заполненность столбцами по
              дням.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
};

export default ColHrDashboard;
